import { Injectable } from '@angular/core';

import type * as ExcelJS from 'exceljs/dist/exceljs.min.js';
import { DownloadService } from '../../services/download.service';
import { ECustomFieldType } from '../custom-fields/custom-field-types-enums';
import { GET_CUSTOM_FIELDS } from '../custom-fields/custom-fields.store';
import { TPoint } from '../points/points.model';
import { GET_TIMELINE } from '../site/site-timeline/timeline.ui.store';
import { addPointRows } from './timeline/add-point-rows';
import { createWorksheet } from './timeline/create-worksheet';
import { getColumnHeaders } from './timeline/get-column-headers';
import { getMinMaxDate } from './timeline/get-min-max-date';
import { styleBasicFields } from './timeline/style-basic-fields';
import { stylePriorityColumn } from './timeline/style-priority-columns';
import { styleTimeline } from './timeline/style-timeline';
import { styleTimelineHeaders } from './timeline/style-timeline-headers';

@Injectable({
  providedIn: 'root',
})
export class TimelineExportService {
  constructor(private downloadService: DownloadService) {}

  async exportLocally(): Promise<ExcelJS.Workbook> {
    const workbook = await createWorksheet();
    const worksheet = workbook.getWorksheet('Timeline Export');
    const points = this.generatePoints();
    const [minDate, maxDate] = getMinMaxDate(points);

    worksheet.state = 'visible';
    worksheet.columns = getColumnHeaders(minDate, maxDate);

    addPointRows(points, worksheet);
    stylePriorityColumn(worksheet);
    styleTimelineHeaders(worksheet);

    styleTimeline(worksheet);
    styleBasicFields(worksheet);
    return this.saveExport(workbook);
  }

  private generatePoints(): TPoint[] {
    const timeline = GET_TIMELINE();
    const items = timeline.items;

    const points = [];

    const timelinePoints = timeline.getPoints();
    items.forEach((item) => {
      const timelinePoint = timelinePoints[item.index];

      if (timelinePoint) {
        for (const key in timelinePoint.customFieldsMap) {
          if (!Object.prototype.hasOwnProperty.call(timelinePoint.customFieldsMap, key)) {
            continue;
          }

          if (GET_CUSTOM_FIELDS()[key].type === ECustomFieldType.TIMELINE) {
            points.push(timelinePoint);
            break;
          }
        }
      }
    });

    return points;
  }

  private async saveExport(workbook: ExcelJS.Workbook): Promise<ExcelJS.Workbook> {
    const data = await workbook.xlsx.writeBuffer();

    const exportData = new Blob([data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    this.downloadService.saveFile(exportData, 'Pinpoint Works Timeline Export');

    return workbook;
  }
}
